import React, { useState } from 'react';
import { ArrowLeft, Globe, Wand2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const ExistingWebsiteAppBuilder = () => {
  const navigate = useNavigate();
  const [url, setUrl] = useState('https://example.com');
  const [appName, setAppName] = useState('My App');
  const [appId, setAppId] = useState('app.snagcart.io');
  const [language, setLanguage] = useState('EN');
  const [adminEmail, setAdminEmail] = useState('');
  const [themeColor, setThemeColor] = useState('#6366F1');
  const [titleColor, setTitleColor] = useState('#FFFFFF');
  const [layoutTemplate, setLayoutTemplate] = useState('drawer_bar');
  const [iconUrl, setIconUrl] = useState('');
  const [splashUrl, setSplashUrl] = useState('');
  const [previewTheme, setPreviewTheme] = useState('dark'); // dark | light
  const [configs, setConfigs] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const token = localStorage.getItem('token');
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchConfigs = async () => {
    if (!token) return;
    try {
      const resp = await fetch(`${API_URL}/api/app-builder/configs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) return;
      const data = await resp.json();
      setConfigs(data || []);
    } catch (err) {
      console.error(err);
    }
  };

  React.useEffect(() => {
    fetchConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setSelectedConfigId(null);
    setAppName('My App');
    setAppId('app.snagcart.io');
    setLanguage('EN');
    setAdminEmail('');
    setThemeColor('#6366F1');
    setTitleColor('#FFFFFF');
    setLayoutTemplate('drawer_bar');
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Please log in');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        app_name: appName,
        website_url: url,
        app_id: appId,
        language,
        admin_email: adminEmail,
        theme_color: themeColor,
        title_color: titleColor,
        layout_template: layoutTemplate,
        icon_url: iconUrl,
        splash_url: splashUrl,
      };
      const method = selectedConfigId ? 'PUT' : 'POST';
      const endpoint = selectedConfigId
        ? `${API_URL}/api/app-builder/configs/${selectedConfigId}`
        : `${API_URL}/api/app-builder/configs`;

      const resp = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to save app config');
      }
      await fetchConfigs();
      toast.success('App configuration saved');
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to save app config');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAndroid = async () => {
    if (!token || !selectedConfigId) return;
    setGenerating(true);
    try {
      const resp = await fetch(`${API_URL}/api/app-builder/generate-android/${selectedConfigId}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to generate Android project');
      }
      const data = await resp.json();
      if (data.download_url) {
        window.open(data.download_url, '_blank');
      }
      toast.success('Android project generated');
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to generate Android project');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen md:pl-64 p-6 md:p-12">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 rounded-full hover:bg-white/5 text-zinc-400"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
              <Wand2 className="w-6 h-6 text-violet-400" />
              App Builder for Existing Website
            </h1>
            <p className="text-zinc-400 text-sm">
              Configure your website as a mobile app, then generate an Android project ready for Play Store.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <div className="glass p-6 rounded-2xl space-y-4 lg:col-span-2">
          <form onSubmit={handleSaveConfig} className="space-y-6">
            <div>
              <label className="block text-xs font-medium mb-1 text-zinc-300">Website URL</label>
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://yourwebsite.com"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium mb-1 text-zinc-300">App name</label>
                <Input
                  value={appName}
                  onChange={(e) => setAppName(e.target.value)}
                  placeholder="My App"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1 text-zinc-300">App ID (package)</label>
                <Input
                  value={appId}
                  onChange={(e) => setAppId(e.target.value)}
                  placeholder="app.snagcart.io"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1 text-zinc-300">Language</label>
                <Input
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  placeholder="EN"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1 text-zinc-300">Admin email</label>
                <Input
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1 text-zinc-300">App icon URL</label>
                <Input
                  value={iconUrl}
                  onChange={(e) => setIconUrl(e.target.value)}
                  placeholder="https://.../icon.png"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1 text-zinc-300">Splash image URL</label>
                <Input
                  value={splashUrl}
                  onChange={(e) => setSplashUrl(e.target.value)}
                  placeholder="https://.../splash.png"
                />
              </div>
            </div>

            <div>
              <p className="block text-xs font-medium mb-1 text-zinc-300">Theme color</p>
              <div className="flex flex-wrap gap-2">
                {['#6366F1', '#EC4899', '#22C55E', '#F97316', '#0EA5E9', '#FACC15'].map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setThemeColor(color)}
                    className={`w-7 h-7 rounded-full border-2 ${
                      themeColor === color ? 'border-white' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            <div>
              <p className="block text-xs font-medium mb-1 text-zinc-300">Title color</p>
              <div className="flex gap-2">
                {['#FFFFFF', '#0F172A', '#F9FAFB'].map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setTitleColor(color)}
                    className={`w-7 h-7 rounded-full border-2 ${
                      titleColor === color ? 'border-white' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            <div>
              <p className="block text-xs font-medium mb-2 text-zinc-300">Layout template</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { id: 'drawer_bar', label: 'Drawer + Bar' },
                  { id: 'tabs_bar', label: 'Tabs + Bar' },
                  { id: 'bar', label: 'Bar' },
                  { id: 'blank', label: 'Blank' },
                ].map((tpl) => (
                  <button
                    key={tpl.id}
                    type="button"
                    onClick={() => setLayoutTemplate(tpl.id)}
                    className={`text-left rounded-xl border px-3 py-2 text-xs transition-colors ${
                      layoutTemplate === tpl.id
                        ? 'border-violet-500 bg-violet-500/10'
                        : 'border-zinc-800 bg-zinc-900/40 hover:border-zinc-600'
                    }`}
                  >
                    <div className="font-semibold text-zinc-100 mb-1">{tpl.label}</div>
                    <p className="text-[11px] text-zinc-400">
                      Simple preset for app chrome and navigation.
                    </p>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 rounded-full px-6"
              >
                <Globe className="w-4 h-4" />
                {loading ? 'Saving...' : 'Save app configuration'}
              </Button>
            </div>
          </form>
        </div>

        <div className="glass p-4 rounded-2xl space-y-4 text-xs text-zinc-400">
          {/* Live app preview */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-[12px]">App preview</span>
              <div className="inline-flex items-center gap-1 text-[10px] bg-zinc-900/60 rounded-full px-2 py-0.5">
                <button
                  type="button"
                  onClick={() => setPreviewTheme('dark')}
                  className={
                    previewTheme === 'dark'
                      ? 'px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-100'
                      : 'px-2 py-0.5 rounded-full text-zinc-400'
                  }
                >
                  Dark
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewTheme('light')}
                  className={
                    previewTheme === 'light'
                      ? 'px-2 py-0.5 rounded-full bg-zinc-200 text-zinc-900'
                      : 'px-2 py-0.5 rounded-full text-zinc-400'
                  }
                >
                  Light
                </button>
              </div>
            </div>
            <div className="flex justify-center">
              <div
                className={`w-64 sm:w-72 rounded-[2rem] border px-3 pt-3 pb-4 shadow-lg transition-colors ${
                  previewTheme === 'dark'
                    ? 'border-zinc-700 bg-zinc-950/90'
                    : 'border-zinc-300 bg-zinc-50'
                }`}
              >
                {/* Icon + top bar */}
                <div
                  className="rounded-2xl px-3 py-1.5 mb-2 flex items-center justify-between gap-2"
                  style={{ backgroundColor: themeColor, color: titleColor }}
                >
                  <div className="flex items-center gap-1.5 min-w-0">
                    {iconUrl && (
                      <img
                        src={iconUrl}
                        alt="App icon"
                        className="w-4 h-4 rounded-md border border-black/10 bg-white/10 flex-shrink-0"
                      />
                    )}
                    <span className="text-[10px] font-semibold truncate">
                      {appName || 'My App'}
                    </span>
                  </div>
                  {layoutTemplate === 'drawer_bar' && (
                    <span className="w-3 h-3 border border-current rounded-sm flex-shrink-0" />
                  )}
                  {layoutTemplate === 'tabs_bar' && (
                    <span className="flex gap-0.5 flex-shrink-0">
                      <span className="w-2 h-2 rounded-full border border-current" />
                      <span className="w-2 h-2 rounded-full border border-current" />
                      <span className="w-2 h-2 rounded-full border border-current" />
                    </span>
                  )}
                  {layoutTemplate === 'bar' && (
                    <span className="w-6 h-1 rounded-full border border-current flex-shrink-0" />
                  )}
                  {layoutTemplate === 'blank' && (
                    <span className="text-[9px] opacity-70 flex-shrink-0">Blank</span>
                  )}
                </div>

                {/* Splash or website content preview */}
                <div
                  className={`rounded-2xl overflow-hidden h-56 flex items-center justify-center text-[10px] transition-colors ${
                    previewTheme === 'dark' ? 'bg-zinc-900 text-zinc-500' : 'bg-white text-zinc-500'
                  }`}
                >
                  {splashUrl ? (
                    <img
                      src={splashUrl}
                      alt="Splash preview"
                      className="w-full h-full object-cover"
                    />
                  ) : url ? (
                    <iframe
                      title="App website preview"
                      src={url}
                      className="w-full h-full border-0"
                    />
                  ) : (
                    <span>Preview of your website inside the app</span>
                  )}
                </div>

                {/* Bottom nav hint */}
                {layoutTemplate !== 'blank' && (
                  <div className="mt-2 flex justify-center gap-1">
                    {layoutTemplate === 'drawer_bar' && (
                      <span className="w-24 h-1 rounded-full bg-zinc-700/80" />
                    )}
                    {layoutTemplate === 'tabs_bar' && (
                      <>
                        <span className="w-8 h-1 rounded-full bg-zinc-700/80" />
                        <span className="w-8 h-1 rounded-full bg-zinc-700/50" />
                        <span className="w-8 h-1 rounded-full bg-zinc-700/30" />
                      </>
                    )}
                    {layoutTemplate === 'bar' && (
                      <span className="w-28 h-1 rounded-full bg-zinc-700/80" />
                    )}
                  </div>
                )}
              </div>
            </div>
            <p className="text-[11px] text-zinc-500 mt-2">
              This is a visual approximation. The generated Android app will wrap your website
              using a full-screen WebView.
            </p>
          </div>

          {/* Saved configs and generate controls */}
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-1">
              <span className="font-semibold text-[12px]">Saved app configs</span>
            </div>
            {configs.length === 0 ? (
              <p className="text-[11px] text-zinc-500">No app configurations yet.</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-auto pr-1">
                {configs.map((cfg) => (
                  <button
                    key={cfg.id}
                    type="button"
                    onClick={() => {
                      setSelectedConfigId(cfg.id);
                      setUrl(cfg.website_url || '');
                      setAppName(cfg.app_name || '');
                      setAppId(cfg.app_id || '');
                      setLanguage(cfg.language || 'EN');
                      setAdminEmail(cfg.admin_email || '');
                      setThemeColor(cfg.theme_color || '#6366F1');
                      setTitleColor(cfg.title_color || '#FFFFFF');
                      setLayoutTemplate(cfg.layout_template || 'drawer_bar');
                      setIconUrl(cfg.icon_url || '');
                      setSplashUrl(cfg.splash_url || '');
                    }}
                    className={`w-full text-left rounded-lg border px-3 py-2 text-[11px] transition-colors ${
                      selectedConfigId === cfg.id
                        ? 'border-violet-500 bg-violet-500/10'
                        : 'border-zinc-800 bg-zinc-900/40 hover:border-zinc-600'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-semibold text-zinc-100 truncate">{cfg.app_name}</span>
                    </div>
                    <p className="text-[10px] text-zinc-500 truncate">{cfg.website_url}</p>
                    <p className="text-[10px] text-zinc-500">{cfg.app_id}</p>
                  </button>
                ))}
              </div>
            )}

            <div className="border-t border-zinc-800 pt-3 mt-2 space-y-2">
              <Button
                type="button"
                disabled={!selectedConfigId || generating}
                onClick={handleGenerateAndroid}
                className="w-full justify-center rounded-full px-4"
              >
                {generating ? 'Generating...' : 'Generate Android project'}
              </Button>
              <p className="text-[11px] text-zinc-500">
                Download the generated project, open in Android Studio, configure signing, and
                upload to Google Play Console.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExistingWebsiteAppBuilder;
